import { KeyRound, Mail, User } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingCard } from "@/components/settings/setting-card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ProfileData } from "../../types";
import { ChangePasswordDialog } from "../change-password-dialog";

interface ProfileInfoCardProps {
  profile: ProfileData;
  className?: string;
}

export function ProfileInfoCard({ profile, className }: ProfileInfoCardProps) {
  const { t } = useTranslation("settings");
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  return (
    <>
      <SettingCard
        title={t("profile.personalInfo")}
        icon={<User className="h-5 w-5" />}
        className={className}
      >
        {/* Profile Avatar and Name */}
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarFallback className="text-lg font-semibold">{profile.initials}</AvatarFallback>
          </Avatar>
          <div className="space-y-1">
            <h3 className="text-xl font-semibold">{profile.fullName}</h3>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="h-4 w-4" />
              <span>{profile.email}</span>
              {profile.emailVerified ? (
                <Badge variant="outline" className="bg-green-50 border-green-200 text-green-700">
                  Verified
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-orange-50 border-orange-200 text-orange-700">
                  Unverified
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* User Details */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              {t("profile.firstName")}
            </label>
            <div className="p-3 bg-muted rounded-md">{profile.firstName}</div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              {t("profile.lastName")}
            </label>
            <div className="p-3 bg-muted rounded-md">{profile.lastName}</div>
          </div>
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium text-muted-foreground">
              {t("profile.email")}
            </label>
            <div className="p-3 bg-muted rounded-md">{profile.email}</div>
          </div>
        </div>

        {/* Change Password Button */}
        <div className="pt-4">
          <Button
            variant="outline"
            onClick={() => setIsChangePasswordOpen(true)}
            className="w-full sm:w-auto"
          >
            <KeyRound className="h-4 w-4 mr-2" />
            {t("profile.changePassword.button")}
          </Button>
        </div>
      </SettingCard>

      <ChangePasswordDialog
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </>
  );
}
